import dayjs from 'dayjs';
import isEmail from 'validator/lib/isEmail';

export const BASE_URL = 'https://api.legentibus.app/v1';  // 'https://api.legentibus.app/staging'

// Server-generated account addresses for students without an email of their
// own. Not the legacy userNNNNN pattern — those are customer accounts and
// must not be resettable by teachers. Keep in sync with
// StudentService::isGeneratedEmail on the backend.
export const isGeneratedEmail = (email) => /^student\d{5}@legentibus\.app$/i.test(email || '');

// A student row is complete with a name and either a valid email address or
// the explicit "no email" mark (the server then generates the credentials).
export const isRowValid = (student) =>
    Boolean(student.name && (student.noEmail || (student.email && isEmail(student.email))));

// Class validity affects seats and student subscriptions, so the backend is
// authoritative. Do not recalculate this from the browser's local date.
export const isClassActive = (schoolClass) => schoolClass?.is_active === true;

// Parse a date-only value (e.g. "2026-08-06") in the user's local time zone.
// The slice keeps this correct even if the server serializes the date as a
// full UTC timestamp. A missing value gives an invalid dayjs object (note
// that dayjs(undefined) would be the current time instead). Not for use on
// real timestamps like last_active_at, whose local day depends on the time.
export const parseDay = (value) => dayjs(value ? String(value).slice(0, 10) : '');

// With forceYear the result is always the exact date: relative words like
// "today" depend on the browser's calendar day, which is not necessarily the
// school's. Class validity boundaries must not shift with the viewer.
const formatParsedDate = (date, forceYear) => {
    if (forceYear) {
        return date.format('MMMM D, YYYY');
    }

    const today = dayjs();
    const tomorrow = dayjs().add(1, 'day');

    if (date.isSame(today, 'day')) {
        return 'today';
    } else if (date.isSame(tomorrow, 'day')) {
        return 'tomorrow';
    } else if (date.isSame(today, 'year')) {
        return date.format('MMMM D');
    } else {
        return date.format('MMMM D, YYYY');
    }
};

export const formatDate = (dateString, forceYear=false) => {
    if (!dateString) {
        return 'never';
    }

    return formatParsedDate(dayjs(dateString), forceYear);
};

// Format a calendar date without applying a timezone offset. Use this for
// date-only API fields; formatDate remains appropriate for real timestamps.
export const formatDay = (dateString, forceYear=false) => {
    if (!dateString) {
        return 'never';
    }

    return formatParsedDate(parseDay(dateString), forceYear);
};

// Class forms hold calendar dates as dayjs values for MUI's date pickers.
// Serialize them to the API's date-only representation, with explicit nulls
// so a cleared boundary is also cleared on the server.
export const classInputToPayload = ({ start_date, end_date, ...otherFields }) => ({
    ...otherFields,
    start_date: start_date ? start_date.format('YYYY-MM-DD') : null,
    end_date: end_date ? end_date.format('YYYY-MM-DD') : null,
});

// Distribute a Laravel validation-errors bag onto the class form's fields;
// false marks a clean field. Messages for fields the form has no error slot
// for (level, term) come back joined under `other`, so callers can show them
// in their general error text instead of dropping them.
export const classFieldErrors = (errors = {}) => ({
    name: errors.name?.[0] || false,
    start_date: errors.start_date?.[0] || false,
    end_date: errors.end_date?.[0] || false,
    other: Object.entries(errors)
        .filter(([field]) => !['name', 'start_date', 'end_date'].includes(field))
        .map(([, messages]) => messages[0])
        .join(' ') || false,
});

// Per-field validation of a class's date range; false marks a valid field.
export const validateClassDateRange = (startDate, endDate) => {
    const errors = { start_date: false, end_date: false };

    if (startDate && !startDate.isValid()) {
        errors.start_date = 'Enter a valid start date';
    }
    if (endDate && !endDate.isValid()) {
        errors.end_date = 'Enter a valid end date';
    }
    if (!errors.start_date && !errors.end_date
        && startDate && endDate && endDate.isBefore(startDate, 'day')) {
        errors.end_date = 'End date must be on or after the start date';
    }

    return errors;
};

// The "from X to Y" fragment describing a class's validity range, with exact
// dates, or null when neither boundary is set.
export const formatDayRange = (startDate, endDate) => {
    if (startDate && endDate) {
        return `from ${formatDay(startDate, true)} to ${formatDay(endDate, true)}`;
    } else if (startDate) {
        return `from ${formatDay(startDate, true)}`;
    } else if (endDate) {
        return `until ${formatDay(endDate, true)}`;
    }
    return null;
};

export const formatNumberWithSpaces = (number, unit=null) => {
  if (typeof number !== 'number') {
    return null;
  }

  const numberString = number.toString();
  const reversedString = numberString.split('').reverse().join('');
  const chunks = reversedString.match(/.{1,3}/g);
  const formattedString = chunks.map(chunk => chunk.split('').reverse().join('')).reverse().join(' ');

  return unit ? formattedString + ' ' + unit : formattedString;
};

function getSexagesimals(time) {
    const seconds = Math.round(Math.max(0, time));
    const s = seconds % 60;
    const m = ((seconds - s) / 60) % 60;
    const h = (seconds - s - 60 * m) / 3600;
    return [h, m, s];
}

export function formatTime(time) {
    if (!time) {
        return null;
    }
    const [h, m, s] = getSexagesimals(time);
    return [
        h,
        m > 9 ? m : '0' + m,  // m : (h ? '0' + m : m || '0'),
        s > 9 ? s : '0' + s
    ].filter(Boolean).join(':');
}

export function formatHumanTime(time) {
    if (!time) {
        return null;
    }
    if (time === 60*60) {
        return '60 min';
    }
    if (time > 0 && time < 60) {
        return '1 min';
    }
    const [h, m] = getSexagesimals(time);
    return [
        h > 0 ? h + ' h' : false,
        m + ' min',
    ].filter(Boolean).join(' ');
}

// Fold text for searching: the catalogue's titles and authors use ligatures
// (Fabulæ), macrons and other diacritics (Rōma, Ørberg), curly apostrophes
// and dashes, which teachers cannot be expected to type. Apply to both the
// haystack and the query before comparing.
export const normalizeForSearch = (text) => (text || '')
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/ø/g, 'o')
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[–—]/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const bookLevelNames = {
    1: 'Beginner',
    2: 'Intermediate',
    3: 'Advanced',
    4: 'Literature',
};

// Difficulty is not a database column; it is derived from the content tags,
// with the same precedence as the consumer app's explore page (AppContext in
// legentibus-appview) so the two products agree. Untagged books count as
// intermediate. The app also falls back on product_id, which the dashboard's
// book list does not receive.
export const deriveBookLevel = (book) => {
    const tags = (book.tags || '').split(';');
    if (tags.includes('beginner')) return 1;
    if (tags.includes('intermediate')) return 2;
    if (tags.includes('advanced')) return 3;
    if (tags.includes('literature')) return 4;
    if ((book.title || '').includes('Beginner')) return 1;
    return 2;
};

export function getPageOffset(book) {
    for (const chapter of book.chapters) {
        if (chapter.duration > 0) {
            return chapter.pages[0].number !== undefined ? chapter.pages[0].number : chapter.pages[0];
        }
        for (const page of chapter.pages) {
            if (page.duration !== undefined && page.duration > 0) {
                return page.number;
            }
        }
    }
    return 0;
}

function getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const dummyData = [
    { last_active_at: '2023-09-19', total_time: 38760, words_read: 145432, assignment: { progress: 100 } },
    { last_active_at: '2023-09-16', total_time: 43320, words_read: 51984, assignment: { progress: 45 } },
    { last_active_at: '2023-09-14', total_time: 22030, words_read: 55075, assignment: { progress: 98 } },
    { last_active_at: '2023-08-23', total_time: 1540, words_read: 3080, assignment: { progress: 0 } },
    { last_active_at: null, total_time: 0, words_read: 0, assignment: { progress: 0 } },
    { last_active_at: '2023-09-05', total_time: 16780, words_read: 50340, assignment: { progress: 78 } },
    { last_active_at: '2023-09-18', total_time: 32140, words_read: 80350, assignment: { progress: 100 } },
    { last_active_at: '2023-08-28', total_time: 27650, words_read: 69035, assignment: { progress: 90 } },
    { last_active_at: '2023-09-15', total_time: 29450, words_read: 76570, assignment: { progress: 92 } },
    { last_active_at: '2023-09-12', total_time: 18940, words_read: 47250, assignment: { progress: 0 } },
    { last_active_at: '2023-09-08', total_time: 14350, words_read: 35930, assignment: { progress: 62 } },
    { last_active_at: '2023-09-10', total_time: 11720, words_read: 29300, assignment: { progress: 12 } },
    { last_active_at: '2023-09-17', total_time: 33860, words_read: 84650, assignment: { progress: 100 } },
    { last_active_at: '2023-08-26', total_time: 23840, words_read: 59700, assignment: { progress: 85 } },
    { last_active_at: null, total_time: 0, words_read: 0, assignment: { progress: 0 } },
    { last_active_at: '2023-08-30', total_time: 26120, words_read: 65280, assignment: { progress: 89 } },
    { last_active_at: '2023-09-07', total_time: 15620, words_read: 39050, assignment: { progress: 70 } },
    { last_active_at: '2023-09-19', total_time: 130, words_read: 3260, assignment: { progress: 57 } },
    { last_active_at: '2023-09-03', total_time: 10170, words_read: 25425, assignment: { progress: 46 } },
    { last_active_at: '2023-09-20', total_time: 41230, words_read: 103075, assignment: { progress: 100 } },
    { last_active_at: '2023-09-11', total_time: 17850, words_read: 44625, assignment: { progress: 72 } },
    { last_active_at: '2023-09-02', total_time: 962, words_read: 24050, assignment: { progress: 43 } },
    { last_active_at: '2023-09-13', total_time: 20340, words_read: 50850, assignment: { progress: 79 } },
    { last_active_at: '2023-09-21', total_time: 42500, words_read: 106250, assignment: { progress: 100 } },
    { last_active_at: '2023-08-25', total_time: 22460, words_read: 56150, assignment: { progress: 83 } },
    { last_active_at: '2023-08-31', total_time: 26730, words_read: 66825, assignment: { progress: 87 } },
    { last_active_at: '2023-09-04', total_time: 11530, words_read: 28825, assignment: { progress: 49 } },
    { last_active_at: '2023-09-06', total_time: 14560, words_read: 36400, assignment: { progress: 66 } },
    { last_active_at: '2023-09-01', total_time: 8090, words_read: 20225, assignment: { progress: 39 } },
    { last_active_at: '2023-08-24', total_time: 21150, words_read: 52875, assignment: { progress: 81 } },
    { last_active_at: '2023-08-29', total_time: 25370, words_read: 63425, assignment: { progress: 86 } },
    { last_active_at: '2023-08-27', total_time: 24610, words_read: 61525, assignment: { progress: 84 } },
].map(item => {
    const minActiveStudyTime = Math.floor(item.total_time / 2);
    const maxActiveStudyTime = item.total_time;
    const activeStudyTime = getRandomInt(minActiveStudyTime, maxActiveStudyTime);

    const readTokens = getRandomInt(item.assignment.progress * 4, item.assignment.progress * 6);

    const studyTime = getRandomInt(Math.floor(readTokens * 0.7), Math.floor(readTokens * 2));

    return {
        ...item,
        active_study_time: activeStudyTime,
        assignment: {
            ...item.assignment,
            read_tokens: readTokens,
            study_time: studyTime,
        }
    };
});
